import * as React from 'react';
import { makeCSS } from '../../utils/makeCSS';
import { SizeVariants } from '../../theme/themes';
import { Typography } from './Typography';
import { cx } from '@emotion/css';
import { Tooltip } from './Tooltip/Tooltip';

/**
 * A visual indicator that displays a number, which represents
 * the elements that are hidden due to the overflow.
 */
export interface OverflowProps {
  /**Receive the size in order to have the same with the rest of the objects in the list*/
  size?: SizeVariants.xs | SizeVariants.sm | SizeVariants.md | SizeVariants.lg;
  /**Choose the type of component it will display an overflow at */
  category?: 'avatar' | 'tag';
  /**The label we want it to display */
  number?: number;
  /**Add a box-shadow, it's needed when the groups overlap*/
  useBoxShadow?: boolean;
  tooltip?: React.ReactNode;
}

const useCSS = makeCSS(({ theme }) => {
  return {
    root: {
      background: theme.color.background.secondary,
      borderRadius: theme.borderRadius.full,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      fontFamily: theme.fontFamilies.body,
      fontVariantNumeric: 'lining-nums tabular-nums slashed-zero',
      fontFeatureSettings: "'ss02' on",
      textRendering: 'optimizeLegibility',
      fontWeight: theme.fontWeights.regular,
      color: theme.color.text.dark['01'],
      boxSizing: 'border-box'
    },
    useBoxShadow: {
      border: `2px solid ${theme.color.background.neutral}`
      // boxSizing: 'border-box'
    },

    //avatar sizes
    'avatar-xs': {
      width: theme.spacing[4],
      height: theme.spacing[4],
      fontSize: theme.fontSize[0]
    },
    'avatar-sm': {
      width: theme.spacing[6],
      height: theme.spacing[6],
      fontSize: theme.fontSize[0]
    },
    'avatar-md': {
      width: theme.spacing[8],
      height: theme.spacing[8],
      fontSize: theme.fontSize[2]
    },
    'avatar-lg': {
      width: theme.spacing[12],
      height: theme.spacing[12],
      fontSize: theme.fontSize[3]
    },

    //tag sizes
    'tag-xs': {
      width: 'fit-content',
      paddingLeft: theme.spacing[1],
      paddingRight: theme.spacing[1],
      height: theme.spacing[5],
      fontSize: theme.fontSize[0]
    },
    'tag-sm': {
      width: 'fit-content',
      minWidth: 24,
      paddingLeft: theme.spacing[1],
      paddingRight: theme.spacing[1],
      height: theme.spacing[6],
      fontSize: theme.fontSize[0]
    },
    'tag-md': {
      width: 'fit-content',
      minWidth: 32,
      paddingLeft: theme.spacing[2],
      paddingRight: theme.spacing[2],
      height: theme.spacing[8],
      fontSize: theme.fontSize[1]
    }
  };
});

export const Overflow = (props: OverflowProps) => {
  const { category, useBoxShadow = true, number, tooltip } = props;
  let { size } = props;

  const classes = useCSS();

  //to avoid breaking the tag
  if (size === 'lg' && category === 'tag') {
    size = SizeVariants.md;
  }

  //make sure the characters doesn't overflow its container
  let displayedNumber = number.toString();
  if (size === 'sm' || size === 'xs') {
    if (number > 9) {
      displayedNumber = '9+';
    }
  } else {
    if (number > 99) {
      displayedNumber = '99+';
    }
  }

  return tooltip ? (
    <Tooltip useArrow={false} content={tooltip}>
      <div
        className={cx(
          classes.root,
          useBoxShadow ? classes.useBoxShadow : '',
          category === 'avatar'
            ? classes[`avatar-${size}`]
            : classes[`tag-${size}`]
        )}
      >
        {displayedNumber}
      </div>
    </Tooltip>
  ) : (
    <div
      className={cx(
        classes.root,
        useBoxShadow ? classes.useBoxShadow : '',
        category === 'avatar'
          ? classes[`avatar-${size}`]
          : classes[`tag-${size}`]
      )}
    >
      {displayedNumber}
    </div>
  );
};
